/* ==========================================================================
   Spark — themed <select>
   --------------------------------------------------------------------------
   SHARED, not PDP-only: the PDP buy box (size + cadence) and the bundle
   builder's delivery schedule both use it, so it lives in its own asset that
   each template loads alongside its own script. It was inline in
   spark-pdp-buy.js until the bundle builder needed the same control.

   Enhances any <select> marked [data-spark-pdp-option] or [data-spark-freq-select]:
   the native control stays in the DOM as the source of truth (it still holds
   the value and still fires `change`); this only draws a nicer face over it,
   and it is applied by JS, so a no-JS visitor gets the plain native dropdown.

   Contract with the markup:
     .spark-pdp-select        the wrapper (gains .spark-sel)
     data-spark-sel-prefix    label drawn inside the closed control
     data-spark-sel-fill      this control takes its width from its row, so the
                              longest-option width reservation stands down
     data-spark-sel-nometa    the closed face leaves out data-meta (the list keeps it)
     data-spark-sel-nosub     the closed face leaves out data-sub (the list keeps it)
     data-meta                per-option secondary column ("20 per pack")
   Styles live in spark-tw/pdp-buy.css (.spark-sel*).
   ========================================================================== */

/* ---------------------------------------------------------------------------
   Custom <select> (data-spark-select)
   ---------------------------------------------------------------------------
   The native control is kept as the source of truth and is never removed — it
   still holds the value, still fires `change`, and every existing code path
   (the select -> radio bridge, syncFreqSelect, resolveChipPlans) keeps working
   untouched. This only draws a nicer face over it and is applied by JS, so a
   no-JS visitor gets the plain native dropdown rather than nothing.

   Why bother: the OS dropdown is the one element on the page we had no control
   over — Chrome renders a hard blue highlight and a system font that belong to
   no design system, and on a page built for 55+ eyes its rows are small.

   The listbox is rebuilt every time it opens, which is deliberate: options can
   be hidden or disabled per variant, so a list cached at init would offer a
   choice the current variant cannot buy.
--------------------------------------------------------------------------- */
(function () {
  var openOne = null;

  function enhance(select) {
    if (select.getAttribute("data-spark-select-ready")) return;
    select.setAttribute("data-spark-select-ready", "1");

    var wrap = select.parentNode;
    wrap.classList.add("spark-sel");
    select.classList.add("spark-sel__native");

    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "spark-sel__btn";
    btn.setAttribute("aria-haspopup", "listbox");
    btn.setAttribute("aria-expanded", "false");
    if (select.getAttribute("aria-label")) {
      btn.setAttribute("aria-label", select.getAttribute("aria-label"));
    }
    // The control reserves the width of its longest choice so it never resizes as
    // the shopper changes selection — see reserveWidth().
    //
    // This used to be a hidden "sizer" element stacked under the label inside
    // .spark-sel__value, which made the VALUE as wide as the longest option. With
    // "Large (38–50 in)" selected, the label's box still ran to the width of
    // "XX-Large (68–80 in)". Invisible, and harmless, until the meta gained its "·"
    // separator: the dot then sat 8px after an invisible box edge, ~40px adrift of
    // the size it was joining, and read as orphaned (Pursarth, 2026-08-07).
    var value = document.createElement("span");
    value.className = "spark-sel__value";
    var current = document.createElement("span");
    current.className = "spark-sel__current";
    value.appendChild(current);
    // Second line, under the value: the size's measurement ("28–40 in"), in caption
    // grey. A size value carries two different kinds of word — the name the shopper
    // chose and the measurement that confirms it — and setting both at one weight on
    // one row made the control read as a sentence to be parsed rather than an answer
    // to be glanced at. The pack count joins this line rather than holding a column
    // of its own, so line one is only ever "Size  Small/Medium".
    var sub = document.createElement("span");
    sub.className = "spark-sel__sub";
    sub.hidden = true;
    value.appendChild(sub);
    // The field's own label, INSIDE the closed control (Pursarth, 2026-08-07: "can we
    // say 'Size Large (38...)' in the drop down?"). The label row above the control is
    // gone, so the word moves into the one place it is still worth having — where it
    // names the value it belongs to rather than floating over it.
    //
    // aria-hidden: the button already carries aria-label="Size" and the fieldset a
    // visually-hidden <legend>, so without it a screen reader announces "Size, Size
    // Large (38 to 50 in)". Purely a visual label.
    //
    // Button only, never the option rows — a list of sizes does not need every line
    // to say "Size".
    var prefixText = wrap.getAttribute("data-spark-sel-prefix");
    if (prefixText) {
      var prefix = document.createElement("span");
      prefix.className = "spark-sel__prefix";
      prefix.setAttribute("aria-hidden", "true");
      // A SHORTER LABEL FOR A NARROW ROW, optional. With data-spark-sel-prefix-short
      // the face carries both ("Deliver every" / "Every") and CSS shows one by the
      // width of the row it sits in (a container query in spark-tw/pdp-buy.css) —
      // which keeps the PDP's cadence link and its badge on one line on a phone.
      var prefixShort = wrap.getAttribute("data-spark-sel-prefix-short");
      if (prefixShort) {
        var longPart = document.createElement("span");
        longPart.className = "spark-sel__prefix-long";
        longPart.textContent = prefixText;
        var shortPart = document.createElement("span");
        shortPart.className = "spark-sel__prefix-short";
        shortPart.textContent = prefixShort;
        prefix.appendChild(longPart);
        prefix.appendChild(shortPart);
      } else {
        prefix.textContent = prefixText;
      }
      btn.appendChild(prefix);
    }
    btn.appendChild(value);
    // Secondary column, e.g. "20 per pack" on the size control. Pushed right so
    // the choices stay in one readable left column.
    var meta = document.createElement("span");
    meta.className = "spark-sel__meta";
    meta.hidden = true;
    btn.appendChild(meta);
    var chev = document.createElement("span");
    chev.className = "spark-sel__chev";
    chev.setAttribute("aria-hidden", "true");
    // 20px, not 14: the arrow is the only thing on the closed control that says
    // it opens, and at 14px it read as punctuation on a 52px-tall field. Heavier
    // stroke with it, so it gains weight as well as size.
    chev.innerHTML =
      '<svg viewBox="0 0 16 16" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6.5 8 10.5 12 6.5"/></svg>';
    btn.appendChild(chev);

    var list = document.createElement("div");
    list.className = "spark-sel__list";
    list.setAttribute("role", "listbox");
    list.hidden = true;

    wrap.appendChild(btn);
    wrap.appendChild(list);

    // A CLOSED FACE CAN SAY LESS THAN ITS LIST. In a bundle-builder card the control is
    // ~165px wide at two columns, where "Small/Medium (28-40 in) · 20 per pack" wrapped to
    // three lines and turned a product tile into a wall of dropdown. An option may carry
    // data-short with the compact form for the face, and a wrapper may set
    // data-spark-sel-nometa to keep the meta column out of the face — the LIST still shows
    // the full label and the meta in both cases, so nothing is hidden from the choice
    // itself, only from the summary of it.
    var showMeta = !wrap.hasAttribute("data-spark-sel-nometa");
    // ONE LINE ON THE PDP. The size face used to print "X-Large" over "48–64 in · 20 per
    // pack": a two-line control in a buy box whose only job up there is to say WHICH size.
    // The range and the count are how a shopper CHOOSES, so they stay on every row of the
    // open list; once chosen, the face says the name and nothing else.
    var showSub = !wrap.hasAttribute("data-spark-sel-nosub");
    function currentText() {
      var o = select.options[select.selectedIndex];
      if (!o) return "";
      return (o.getAttribute("data-short") || o.textContent).trim();
    }
    function refresh() {
      current.textContent = currentText();
      var selOpt = select.options[select.selectedIndex];
      var metaText = showMeta && selOpt ? selOpt.getAttribute("data-meta") : null;
      var subText = showSub && selOpt ? selOpt.getAttribute("data-sub") : null;
      // With a sub line present the meta joins it and the meta COLUMN stands down:
      // "20 per pack" hanging beside a two-line value puts the count back on line
      // one, which is the line the split exists to clear.
      if (subText && metaText) {
        subText = subText + " · " + metaText;
        metaText = null;
      }
      sub.textContent = subText || "";
      sub.hidden = !subText;
      btn.classList.toggle("spark-sel__btn--stacked", !!subText);
      meta.textContent = metaText || "";
      meta.hidden = !metaText;
      // Re-reserve: options can be hidden or relabelled per variant, so the
      // longest available label can change when the size does.
      reserveWidth();
    }

    // Hold the button at the width its longest option would need, so choosing a
    // shorter size doesn't shrink the control.
    //
    // Measured DIRECTLY: put each label into the real element, read the real
    // button, keep the largest. Six reflows once per refresh, and no arithmetic to
    // be wrong about.
    //
    // The arithmetic version (widestLabel + (btn.offsetWidth - value.offsetWidth))
    // was wrong and shipped for one push: `btn.offsetWidth` is not the button's
    // content width — the wrapper is width:fit-content and resolves against the
    // whole control, so "everything that isn't the label" measured ~357px instead
    // of the ~160px of padding, gaps, meta and chevron that are actually there.
    // Each refresh then reserved the last reservation PLUS a label, and the
    // control grew every time the size changed: 357px, 549px, and climbing.
    // Measuring the thing you want beats deriving it from two things you don't.
    function reserveWidth() {
      btn.style.minWidth = "";
      // A select that FILLS its row takes its width from the row, not from its
      // longest option — so there is nothing to reserve, and a min-width here would
      // stop it shrinking to fit its flex track. The size control on the PDP is laid
      // out beside "What's my size?" this way (snippets/spark-pdp-buybox.liquid).
      //
      // ...EXCEPT a one-line face (data-spark-sel-nosub) that fills its row: there the
      // reservation goes on the WRAPPER as a min-width, measured at the face's natural
      // width. It is what lets the row decide honestly between one line and two — the
      // size control and "What's my size?" share a line only where the widest size name
      // fits beside the link — and it stops the row re-flowing when a shorter size is
      // chosen ("Large" would fit where "Small/Medium" did not).
      if (wrap.hasAttribute("data-spark-sel-fill")) {
        if (showSub) return;
        wrap.style.minWidth = "";
        if (!btn.offsetWidth) return;
        var keepFill = current.textContent;
        var widestFill = 0;
        btn.style.width = "max-content";
        for (var k = 0; k < select.options.length; k += 1) {
          var ok = select.options[k];
          if (ok.disabled && ok.value !== "") continue;
          current.textContent = (ok.getAttribute("data-short") || ok.textContent).trim();
          if (btn.offsetWidth > widestFill) widestFill = btn.offsetWidth;
        }
        btn.style.width = "";
        current.textContent = keepFill;
        var room = wrap.parentElement ? wrap.parentElement.clientWidth : 0;
        if (room && widestFill > room) widestFill = room;
        if (widestFill) wrap.style.minWidth = Math.ceil(widestFill) + "px";
        return;
      }
      // Detached or display:none — every measurement is 0 and we would pin the
      // button to a garbage width. The next refresh re-runs this.
      if (!btn.offsetWidth) return;
      var keep = current.textContent;
      var widest = 0;
      for (var i = 0; i < select.options.length; i += 1) {
        var o = select.options[i];
        if (o.hidden || o.disabled) continue;
        current.textContent = (o.getAttribute("data-short") || o.textContent).trim();
        if (btn.offsetWidth > widest) widest = btn.offsetWidth;
      }
      current.textContent = keep;
      // Never reserve more than the control actually has. The widest option is a
      // property of the PRODUCT; the available width is a property of the SCREEN,
      // and on a 320px phone the frequency control's longest cadence is wider than
      // the whole buy column. Reserving it there pushed the card, the panel grid and
      // the page into horizontal scroll. Capped, the label truncates instead — which
      // is what the ellipsis on .spark-sel__current is for.
      var avail = wrap.parentElement ? wrap.parentElement.clientWidth : 0;
      if (avail && widest > avail) widest = avail;
      if (widest) btn.style.minWidth = Math.ceil(widest) + "px";
    }

    select._sparkSelRefresh = refresh;
    refresh();
    // Fonts land after first paint and every width above is text — remeasure once
    // they have, or the reservation is sized in the fallback face.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(reserveWidth).catch(function () {});
    }

    function build() {
      list.innerHTML = "";
      for (var i = 0; i < select.options.length; i += 1) {
        var o = select.options[i];
        if (o.hidden || o.disabled) continue;
        var item = document.createElement("button");
        item.type = "button";
        item.className = "spark-sel__opt";
        item.setAttribute("role", "option");
        item.setAttribute("aria-selected", i === select.selectedIndex ? "true" : "false");
        item.setAttribute("data-index", String(i));
        var label = document.createElement("span");
        label.className = "spark-sel__optlabel";
        var oSub = o.getAttribute("data-sub");
        var m = o.getAttribute("data-meta");
        // Same split as the closed face: the size NAME on its own line, the
        // measurement beneath it in caption grey, with the pack count joined onto
        // that second line rather than centred beside a two-line row (where its
        // separator dot floats between the two lines it belongs to neither of).
        // Only when the option carries a sub — otherwise the row keeps the flat
        // structure it has always had, so cadence lists and the bundle-builder
        // cards are untouched.
        if (oSub) {
          label.textContent = (o.getAttribute("data-short") || o.textContent).trim();
          var subEl = document.createElement("span");
          subEl.className = "spark-sel__sub";
          subEl.textContent = m ? oSub + " · " + m : oSub;
          label.appendChild(subEl);
          m = null;
        } else {
          label.textContent = o.textContent.trim();
        }
        item.appendChild(label);
        if (m) {
          var mEl = document.createElement("span");
          mEl.className = "spark-sel__meta";
          mEl.textContent = m;
          item.appendChild(mEl);
        }
        list.appendChild(item);
      }
    }

    function close(focusBtn) {
      if (list.hidden) return;
      list.hidden = true;
      btn.setAttribute("aria-expanded", "false");
      openOne = null;
      if (focusBtn) btn.focus();
    }
    function open() {
      if (openOne && openOne !== close) openOne(false);
      build();
      list.hidden = false;
      btn.setAttribute("aria-expanded", "true");
      openOne = close;
      var sel = list.querySelector('[aria-selected="true"]') || list.firstChild;
      if (sel && sel.focus) sel.focus();
    }
    function choose(index) {
      if (select.selectedIndex !== index) {
        select.selectedIndex = index;
        // The bridge in this file listens for `change`; a programmatic value
        // set does not fire one, so it has to be dispatched by hand.
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }
      refresh();
      close(true);
    }

    btn.addEventListener("click", function () {
      if (list.hidden) open(); else close(true);
    });
    btn.addEventListener("keydown", function (e) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        open();
      }
    });
    list.addEventListener("click", function (e) {
      var opt = e.target.closest(".spark-sel__opt");
      if (opt) choose(parseInt(opt.getAttribute("data-index"), 10));
    });
    list.addEventListener("keydown", function (e) {
      var items = [].slice.call(list.querySelectorAll(".spark-sel__opt"));
      var at = items.indexOf(document.activeElement);
      if (e.key === "Escape") { e.preventDefault(); close(true); }
      else if (e.key === "ArrowDown") { e.preventDefault(); (items[at + 1] || items[0]).focus(); }
      else if (e.key === "ArrowUp") { e.preventDefault(); (items[at - 1] || items[items.length - 1]).focus(); }
      else if (e.key === "Home") { e.preventDefault(); items[0].focus(); }
      else if (e.key === "End") { e.preventDefault(); items[items.length - 1].focus(); }
      else if (e.key === "Tab") { close(false); }
    });
    document.addEventListener("click", function (e) {
      if (!list.hidden && !wrap.contains(e.target)) close(false);
    });
    // Value can change without a click (recovery paths, syncFreqSelect).
    select.addEventListener("change", refresh);
  }

  // Cadence/supply AND size. The size control drives variant selection through
  // the same `change` event the native one fired, so nothing downstream changes
  // — and leaving it as the one OS-rendered dropdown in a column of custom ones
  // was more jarring than the default had been on its own.
  document
    .querySelectorAll("[data-spark-freq-select], [data-spark-pdp-option]")
    .forEach(function (el) {
      if (el.tagName === "SELECT") enhance(el);
    });
})();
